import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { BADGE_SX, SURFACE_SX, formatValue } from './customerDetailUtils';

const FORM_CONTAINER_SX = {
  p: 1.5,
  borderRadius: 3,
  border: '1px solid rgba(0,0,0,0.07)',
  backgroundColor: '#fafcfc',
};

const CustomerDetailContactsSection = memo(function CustomerDetailContactsSection({
  activeCount,
  archivedCount,
  error,
  formMode,
  formValues,
  isRefreshingContacts,
  isSaving,
  items,
  onCancel,
  onFieldChange,
  onStartCreate,
  onStartEdit,
  onSubmit,
  onToggleActive,
  onToggleArchived,
  showArchivedContacts,
}) {
  const contactSummary = `${activeCount} active${archivedCount ? ` • ${archivedCount} archived` : ''}`;

  return (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Contacts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keep shared customer people separate from historical activity. {contactSummary}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={onToggleArchived}
              disabled={isRefreshingContacts}
              sx={{ textTransform: 'none' }}
            >
              {isRefreshingContacts
                ? 'Loading...'
                : showArchivedContacts
                  ? 'Hide archived'
                  : 'Show archived'}
            </Button>
            <Button
              type="button"
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon fontSize="small" />}
              onClick={onStartCreate}
              sx={{ textTransform: 'none' }}
            >
              Add contact
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {formMode && (
          <Box component="form" onSubmit={onSubmit} sx={FORM_CONTAINER_SX}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                {formMode === 'create' ? 'Add contact' : 'Edit contact'}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                <TextField
                  label="Contact name"
                  value={formValues.name}
                  onChange={onFieldChange('name')}
                  size="small"
                  fullWidth
                  required
                />
                <TextField
                  label="Contact title"
                  value={formValues.title}
                  onChange={onFieldChange('title')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Contact phone"
                  value={formValues.phone}
                  onChange={onFieldChange('phone')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Contact email"
                  value={formValues.email}
                  onChange={onFieldChange('email')}
                  size="small"
                  fullWidth
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.isPrimary}
                    onChange={onFieldChange('isPrimary')}
                    size="small"
                  />
                }
                label="Primary contact"
              />

              <TextField
                label="Contact notes"
                value={formValues.notes}
                onChange={onFieldChange('notes')}
                size="small"
                multiline
                minRows={3}
                fullWidth
              />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving}
                  sx={{ textTransform: 'none' }}
                >
                  {isSaving
                    ? 'Saving...'
                    : formMode === 'create'
                      ? 'Create contact'
                      : 'Save contact'}
                </Button>
                <Button type="button" variant="text" onClick={onCancel} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {showArchivedContacts
              ? 'No contacts saved for this account yet.'
              : 'No active contacts saved for this account yet.'}
          </Typography>
        ) : (
          <Stack spacing={1.15}>
            {items.map((contact) => (
              <Box
                key={contact.id}
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: contact.active ? '#fcfdfd' : '#fafafa',
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: contact.active ? 'text.primary' : 'text.secondary' }}
                      >
                        {contact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatValue(contact.title)}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {contact.isPrimary && (
                        <Box
                          component="span"
                          sx={{
                            ...BADGE_SX,
                            color: '#0d47a1',
                            backgroundColor: 'rgba(13,71,161,0.08)',
                            borderColor: 'rgba(13,71,161,0.16)',
                          }}
                        >
                          Primary
                        </Box>
                      )}
                      {!contact.active && (
                        <Box
                          component="span"
                          sx={{
                            ...BADGE_SX,
                            color: 'text.secondary',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            borderColor: 'rgba(0,0,0,0.08)',
                          }}
                        >
                          Archived
                        </Box>
                      )}
                    </Stack>
                  </Stack>

                  {(contact.phone || contact.email) && (
                    <Stack spacing={0.35}>
                      {contact.phone && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {contact.phone}
                        </Typography>
                      )}
                      {contact.email && (
                        <Typography variant="body2" color="text.secondary">
                          Email: {contact.email}
                        </Typography>
                      )}
                    </Stack>
                  )}

                  {contact.notes && (
                    <Typography variant="body2" color="text.secondary">
                      {contact.notes}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      type="button"
                      size="small"
                      variant="text"
                      startIcon={<EditRoundedIcon fontSize="small" />}
                      onClick={() => onStartEdit(contact)}
                      disabled={isSaving}
                      sx={{ textTransform: 'none' }}
                    >
                      Edit {contact.name}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      startIcon={
                        contact.active ? (
                          <ArchiveRoundedIcon fontSize="small" />
                        ) : (
                          <RestoreRoundedIcon fontSize="small" />
                        )
                      }
                      onClick={() => onToggleActive(contact, !contact.active)}
                      disabled={isSaving}
                      sx={{ textTransform: 'none' }}
                    >
                      {contact.active ? `Archive ${contact.name}` : `Reactivate ${contact.name}`}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
});

export default CustomerDetailContactsSection;
